import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const PaymentCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your payment...');

  useEffect(() => {
    const handlePaymentCallback = async () => {
      try {
        const registrationId = searchParams.get('registrationId');
        
        if (!registrationId) {
          setStatus('error');
          setMessage('Invalid payment callback: missing registration ID');
          return;
        }

        // Retrieve registration data from sessionStorage (stored before payment window opened)
        const storedDataJson = sessionStorage.getItem(`registration_${registrationId}`);
        
        if (!storedDataJson) {
          setStatus('error');
          setMessage('Unable to retrieve registration data. Please close this window and try again.');
          return;
        }

        const registrationData = JSON.parse(storedDataJson);

        // Send receipt email automatically
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentReceipt: true,
            registrationId: registrationData.registrationId,
            contactInfo: registrationData.contactInfo,
            billingInfo: registrationData.billingInfo,
            primarySponsor: registrationData.primarySponsor,
            totalAmount: registrationData.totalAmount,
            teamName: registrationData.teamName,
          }),
        });

        if (!emailResponse.ok) {
          const result = await emailResponse.json();
          throw new Error(result.error || 'Failed to send receipt email');
        }

        setStatus('success');
        setMessage('Payment successful! Receipt email has been sent.');

        // Clean up stored data
        sessionStorage.removeItem(`registration_${registrationId}`);

        // Close window after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'An error occurred processing your payment');
      }
    };

    handlePaymentCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Processing Payment</h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Payment Received!</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">This window will close automatically in a few seconds.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
              <ExclamationCircleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Error Processing Payment</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => window.close()}
              className="mt-4 px-6 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition"
            >
              Close Window
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
